import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Eye, EyeOff } from "lucide-react";
import { useToast } from "../../../context/ToastContext";
import { getErrorMessage } from "../../../api/client";
import * as emailTemplatesApi from "../../../api/emailTemplates";
import { EmailTemplate } from "../../../api/emailTemplates";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Badge } from "../../../components/ui/Badge";
import { EmailPreview } from "./EmailPreview";

// Placeholder disponibili per ciascun template — devono corrispondere
// esattamente a SAMPLE_VARS in backend/src/routes/emailTemplates.ts.
const VARIABILI_DISPONIBILI: Record<string, string[]> = {
  invito: ["{{link}}"],
  // {{identificativo}} è la credenziale con cui l'utente accede davvero
  // (telefono o email, secondo il suo metodo di accesso). {{email}} resta come
  // alias dello stesso valore, per i template già salvati che lo usano.
  credenziali: ["{{identificativo}}", "{{email}}", "{{password}}"],
  credenziali_reset: ["{{identificativo}}", "{{email}}", "{{password}}"],
};

type FormValues = {
  oggetto: string;
  corpo_html: string;
  attivo: boolean;
};

export default function EmailTemplates() {
  const { showToast } = useToast();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedChiave, setSelectedChiave] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);

  async function load() {
    setIsLoading(true);
    try {
      const { templates: list } = await emailTemplatesApi.listEmailTemplates();
      setTemplates(list);
      if (!selectedChiave && list.length > 0) setSelectedChiave(list[0].chiave);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare i template."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = templates.find((t) => t.chiave === selectedChiave) ?? null;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting, isDirty },
  } = useForm<FormValues>();

  useEffect(() => {
    if (selected) {
      reset({ oggetto: selected.oggetto, corpo_html: selected.corpo_html, attivo: selected.attivo });
      setPreviewHtml(null);
      setShowPreview(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.chiave]);

  const onSubmit = handleSubmit(async (values) => {
    if (!selected) return;
    try {
      await emailTemplatesApi.updateEmailTemplate(selected.chiave, values);
      showToast("Template salvato.", "success");
      load();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare il template."), "error");
    }
  });

  async function handlePreview() {
    if (!selected) return;
    setIsPreviewing(true);
    try {
      const values = watch();
      const result = await emailTemplatesApi.previewEmailTemplate(selected.chiave, values.oggetto, values.corpo_html);
      setPreviewHtml(result.corpo_html);
      setShowPreview(true);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile generare l'anteprima."), "error");
    } finally {
      setIsPreviewing(false);
    }
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-ink">Template Email</h1>
        <p className="mt-1 text-sm text-ink/50">
          Testi usati per le email automatiche (invito, credenziali operatore).
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm text-ink/40">Caricamento…</p>
      ) : templates.length === 0 ? (
        /* Senza questo messaggio la pagina appare semplicemente vuota, e non
           c'è modo di capire se sia un guasto, un problema di permessi o una
           configurazione mancante. I template sono righe di database create
           dal seed, non dall'interfaccia: dove il seed non è stato eseguito
           non c'è nulla da elencare, e le email continuano a partire con i
           testi incorporati nel codice. */
        <div className="rounded-card border border-line bg-white p-6 shadow-card">
          <h2 className="text-base font-bold text-ink">Nessun template personalizzato</h2>
          <p className="mt-2 max-w-2xl text-sm text-ink/70">
            Le comunicazioni automatiche usano i <strong>testi predefiniti del sistema</strong>, incorporati
            nell'applicazione. Le email partono regolarmente: non c'è nulla di rotto e non serve alcun intervento.
          </p>
          <p className="mt-3 text-sm text-ink/70">Riguarda gli invii di:</p>
          <ul className="mt-1 list-inside list-disc text-sm text-ink/70">
            <li>invito a registrarsi</li>
            <li>credenziali di un nuovo operatore</li>
            <li>credenziali dopo un reset della password</li>
            <li>link per il recupero password</li>
          </ul>
          <p className="mt-4 max-w-2xl text-xs text-ink/50">
            Questa pagina permette di modificare i testi, non di crearli: compaiono qui solo i template già presenti
            in archivio. Per personalizzarne uno è necessario che venga prima creato lato sistema.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_1fr]">
          {/* --- Elenco template --- */}
          <div className="flex flex-col gap-2">
            {templates.map((t) => (
              <button
                key={t.chiave}
                onClick={() => setSelectedChiave(t.chiave)}
                className={`flex items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
                  t.chiave === selectedChiave
                    ? "border-primary bg-primary/5 font-semibold text-primary"
                    : "border-line bg-white text-ink hover:bg-black/5"
                }`}
              >
                {t.nome}
                <Badge color={t.attivo ? "success" : "neutral"}>{t.attivo ? "Attivo" : "Disattivo"}</Badge>
              </button>
            ))}
          </div>

          {/* --- Editor --- */}
          {selected && (
            <div className="rounded-card border border-line bg-white p-5 shadow-card">
              <form id="template-form" onSubmit={onSubmit} className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-ink">
                    <input type="checkbox" className="h-4 w-4 rounded border-black/20" {...register("attivo")} />
                    Template attivo
                  </label>
                  <p className="text-xs text-ink/40">
                    Se disattivato, l'app usa il testo di riserva incorporato nel codice.
                  </p>
                </div>

                <Input label="Oggetto" required {...register("oggetto", { required: true })} />

                <div className="rounded-lg bg-primary/5 px-3 py-2 text-xs text-ink/70">
                  Variabili disponibili per questo template:{" "}
                  <span className="font-mono font-semibold">
                    {(VARIABILI_DISPONIBILI[selected.chiave] ?? []).join(", ")}
                  </span>
                  — verranno sostituite con il valore reale al momento dell'invio.
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-ink">Corpo (HTML)</label>
                  <textarea
                    rows={14}
                    className="rounded-lg border border-line bg-white px-3 py-2 font-mono text-xs text-ink"
                    {...register("corpo_html", { required: true })}
                  />
                </div>

                <div className="flex items-center justify-between border-t border-line pt-4">
                  <Button type="button" variant="secondary" loading={isPreviewing} onClick={handlePreview}>
                    {showPreview ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                    Anteprima
                  </Button>
                  <Button type="submit" loading={isSubmitting} disabled={!isDirty}>
                    Salva
                  </Button>
                </div>
              </form>

              {showPreview && previewHtml && (
                <div className="mt-4 border-t border-line pt-4">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ink/50">
                    Anteprima con dati di esempio
                  </p>
                  <EmailPreview html={previewHtml} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
